// Design tokens for the chat dialog and the video call.
//
// These are called EXPLICITLY from each theme file instead of being defined
// once in classicThemeVariables, because darkTheme.js and lightTheme.js are
// both built as merge({}, classicThemeVariables, {...}): anything defined only
// in classic is silently inherited by the other two. That is exactly how
// component.commentMain / commentSecondary ended up painting classic's bright
// cyan bubbles in all three themes — dark and light each override ~55
// component.* keys, including those two tokens' immediate neighbours, but
// missed these. Passing the values in per theme turns "which themes have to
// supply this" into an argument list, so adding a token without deciding its
// value for every theme fails at the call site rather than silently inheriting.
//
// Bubble tokens carry their own foreground colour alongside the background.
// The background and the text colour have to be chosen together to hit 4.5:1,
// so keeping them in one object is what stops them drifting apart again.

/**
 * Chat tokens for theme.component.*. Every argument is required — see above.
 */
export const chatComponentTokens = ({
  bubbleOwn,
  bubbleOwnText,
  bubbleOwnBorder,
  bubbleOther,
  bubbleOtherText,
  bubbleOtherBorder,
  avatarBackground,
  daySeparator,
  controlOutline,
}) => ({
  chatBubbleOwn: {
    backgroundColor: bubbleOwn,
    color: bubbleOwnText,
    border: bubbleOwnBorder,
  },
  chatBubbleOther: {
    backgroundColor: bubbleOther,
    color: bubbleOtherText,
    border: bubbleOtherBorder,
  },
  chatAvatar: { backgroundColor: avatarBackground },
  chatDaySeparator: { color: daySeparator },
  chatControlOutline: { borderColor: controlOutline },
});

// The call surface stays dark in every theme, the way Meet, Zoom and Teams do
// it: the video *is* the interface, and a light surround next to arbitrary
// camera content hurts perceived contrast and flares on a dark call. So unlike
// the chat tokens above, these are deliberately theme-independent — the same
// object is assigned in all three themes. That is intentional, not the
// inheritance bug this file exists to prevent.
export const videoChatPalette = {
  error: '#f44336',
  neutral: '#3a3b40',
  // White glyphs on #4caf50 measured 2.78:1, under the 3:1 floor for
  // graphical objects; #2e7d32 gives 5.13:1.
  success: '#2e7d32',
  successDark: '#1b5e20',
  surface: '#1b1c20',
  surfaceRaised: '#26272c',
  controlBar: '#141519',
  badgeBorder: '#1a1a1a',
  onSurface: '#ffffff',
  onSurfaceMuted: 'rgba(255,255,255,0.70)',
  divider: 'rgba(255,255,255,0.12)',
  hover: 'rgba(255,255,255,0.08)',
};
